import { useCallback } from "react";
import {
    buildMaterialLine,
    getDefaultLineProduct,
    isMultiLineCategory,
    isMultiLineSelection,
    type MaterialLine,
} from "./utils/materialLineHelpers";
import { findManufacturerProductId, findSubcategory } from "./utils/productHelpers";

interface UseMaterialLineUpdateProps {
    materialSchema: any[];
    setSelectedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

export function useMaterialLineUpdate({ materialSchema, setSelectedValues }: UseMaterialLineUpdateProps) {
    const apply = useCallback(
        (updater: (prev: Record<string, any>) => Record<string, any>) => {
            setSelectedValues((prev) => updater(prev));
        },
        [setSelectedValues],
    );

    const getProductOptions = useCallback(
        (category: string): string[] => {
            const schema = materialSchema.find((item) => item.Name === category);
            const subs = schema?.Subcategories || [];
            return Array.from(new Set<string>(subs.map((s: any) => s.Name).filter(Boolean)));
        },
        [materialSchema],
    );

    const handleLineProductChange = useCallback(
        (category: string, lineKey: string, product: string) => {
            if (!isMultiLineCategory(category) || !product) return;

            apply((prev) => {
                const sel = prev[category];
                if (!isMultiLineSelection(sel)) return prev;

                const lines = sel.lines.map((line) => {
                    if (line.lineKey !== lineKey) return line;

                    const sub = findSubcategory(materialSchema, category, product);
                    const manufacturerProductId = findManufacturerProductId(materialSchema, category, product);
                    const productChanged = line.value !== product;

                    return {
                        lineKey: product,
                        value: product,
                        quantity: line.quantity || 1,
                        manufacturerProductId,
                        systemLine: sub?.SystemLine || product,
                        id: productChanged ? undefined : line.id,
                        supplierId: productChanged ? undefined : line.supplierId,
                        supplierName: productChanged ? undefined : line.supplierName,
                        unitPrice: productChanged ? undefined : line.unitPrice,
                    };
                });

                return { ...prev, [category]: { multiLine: true, lines } };
            });
        },
        [apply, materialSchema],
    );

    const handleLineQuantityUpdate = useCallback(
        (category: string, lineKey: string, direction: "add" | "remove") => {
            apply((prev) => {
                const sel = prev[category];
                if (!isMultiLineSelection(sel)) return prev;

                const lines = sel.lines.map((line) => {
                    if (line.lineKey !== lineKey) return line;
                    const currentQty = line.quantity || 0;
                    const newQty = direction === "add" ? currentQty + 1 : Math.max(0, currentQty - 1);
                    return { ...line, quantity: newQty };
                });

                return { ...prev, [category]: { multiLine: true, lines } };
            });
        },
        [apply],
    );

    const handleAddLine = useCallback(
        (category: string) => {
            if (!isMultiLineCategory(category)) return;

            apply((prev) => {
                const productOptions = getProductOptions(category);
                const existing: MaterialLine[] = isMultiLineSelection(prev[category]) ? prev[category].lines : [];
                const used = new Set<string>(existing.map((line) => line.value));
                const product = getDefaultLineProduct(productOptions, used);
                if (!product) return prev;

                const line = buildMaterialLine(materialSchema, category, product, 1);
                if (!line) return prev;

                const lines: MaterialLine[] = [...existing, line];
                return { ...prev, [category]: { multiLine: true, lines } };
            });
        },
        [apply, getProductOptions, materialSchema],
    );

    const handleRemoveLine = useCallback(
        (category: string, lineKey: string) => {
            apply((prev) => {
                const sel = prev[category];
                if (!isMultiLineSelection(sel)) return prev;

                const lines = sel.lines.filter((line) => line.lineKey !== lineKey);
                if (lines.length === 0) {
                    const next = { ...prev };
                    delete next[category];
                    return next;
                }

                return { ...prev, [category]: { multiLine: true, lines } };
            });
        },
        [apply],
    );

    const handleClearMultiLine = useCallback(
        (category: string) => {
            apply((prev) => {
                const next = { ...prev };
                delete next[category];
                return next;
            });
        },
        [apply],
    );

    return {
        handleLineProductChange,
        handleLineQuantityUpdate,
        handleAddLine,
        handleRemoveLine,
        handleClearMultiLine,
    };
}
